// The facts a voice row states beside its title: when it was recorded, how its sound was
// graded and how much speech it holds. Shared by the Voices card, the Unassigned voices list
// and the identity's recording rows, so all of them say the same things the same way.
import { useTranslation } from 'react-i18next'
import { numberFormat } from '@/components/humint/format'
import { verdictLine } from '@/components/humint/voice/voice-sample-quality'
import { toVoiceSampleQuality } from '@/features/humint/mappers/subject-page'
import type { IdentityVoiceprint } from '@/services/api/identity-voices'

/** A timestamp as the identity page writes it; nothing for none. */
export function VoiceWhen({ iso }: { iso: string | null }) {
  const { i18n } = useTranslation()
  if (!iso) return null
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return null
  return (
    <p className="text-sm text-muted-foreground">
      <time dateTime={iso}>{at.toLocaleString(i18n.language)}</time>
    </p>
  )
}

/** Seconds of speech in words: "42 seconds of speech"; nothing for an unknown count. */
export function SpeechSeconds({ seconds }: { seconds: number | null }) {
  const { t, i18n } = useTranslation()
  if (seconds === null || !Number.isFinite(seconds)) return null
  const shown = numberFormat(i18n.language, { maximumFractionDigits: 1 }).format(seconds)
  return (
    <p className="text-sm tabular-nums text-muted-foreground">
      {t('identity.detail.audio.speech', { seconds: shown })}
    </p>
  )
}

/** The worker's grade with its reasons, then the speech; the speech alone for an ungraded voiceprint. */
export function VoiceQualityLine({
  voiceprint,
}: {
  voiceprint: Pick<IdentityVoiceprint, 'quality' | 'quality_flags' | 'speech_seconds' | 'reference'>
}) {
  const { t } = useTranslation()
  const quality = voiceprint.quality
    ? toVoiceSampleQuality({
        grade: voiceprint.quality,
        flags: voiceprint.quality_flags,
        speech_seconds: voiceprint.speech_seconds ?? 0,
        reference: voiceprint.reference,
      })
    : null
  return (
    <div className="space-y-0.5 text-sm">
      {quality ? <p className="text-muted-foreground">{verdictLine(quality, t)}</p> : null}
      <SpeechSeconds seconds={voiceprint.speech_seconds} />
    </div>
  )
}
